// src/constants/accessGuard.ts
var ACCESS_GUARD = {
  UNREGISTERED_COURSE_ACCESS: "unregisteredCourseAccess",
  UNREGISTERED_QA_ACCESS: "unregisteredQAAccess",
  UNREGISTERED_NEWS_ACCESS: "unregisteredNewsAccess",
  UNREGISTERED_ARTICLES_ACCESS: "unregisteredArticlesAccess"
};

// src/constants/aiMentorAvatar.ts
var ALLOWED_AVATAR_IMAGE_TYPES = ["image/jpeg", "image/png", "image/svg+xml", "image/jpg"];
var ALLOWED_EXTENSIONS = ".jpeg,.png,.svg,.jpg";

// src/constants/aiMentorTypes.ts
var AI_MENTOR_TYPE = {
  MENTOR: "mentor",
  TEACHER: "teacher",
  ROLEPLAY: "roleplay"
};

// src/constants/allowedAge.ts
var ALLOWED_AGE_LIMITS = [13, 16, null];

// src/constants/articlesSettings.ts
var ALLOWED_ARTICLES_SETTINGS = {
  ARTICLES_ENABLED: "articlesEnabled",
  UNREGISTERED_USER_ARTICLES_ACCESSIBILITY: "unregisteredUserArticlesAccessibility"
};

// src/constants/articlesTypes.ts
var ARTICLE_STATUS = {
  PUBLISHED: "published",
  DRAFT: "draft"
};

// src/constants/authorizationErrorCodes.ts
var AUTHORIZATION_ERROR_CODES = {
  /** Course has lessonSequenceEnabled and a previous lesson is not completed yet. */
  LESSON_SEQUENCE_LOCKED: "LESSON_SEQUENCE_LOCKED",
  /** Lesson belongs to a course the student has not purchased and is not freemium. */
  LESSON_NOT_PURCHASED: "LESSON_NOT_PURCHASED",
  /** Chapter belongs to a course the student has no access to and is not freemium. */
  CHAPTER_NOT_PURCHASED: "CHAPTER_NOT_PURCHASED",
  /** Endpoint requires the admin role. */
  ADMIN_ROLE_REQUIRED: "ADMIN_ROLE_REQUIRED"
};

// src/constants/certificateCode.ts
var CERTIFICATE_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
var CERTIFICATE_CODE_LENGTH = 12;
var CERTIFICATE_CODE_GROUP_SIZE = 4;
var CERTIFICATE_CODE_SEPARATOR = "\xB7";
var CONFUSABLE_CHARS = {
  I: "1",
  L: "1",
  O: "0",
  U: "V"
  // Crockford descarta la U; quien la escribe casi siempre quiso una V.
};
function normalizeCertificateCode(input) {
  if (!input) return null;
  const stripped = input.toUpperCase().replace(/[^0-9A-Z]/g, "").split("").map((char) => CONFUSABLE_CHARS[char] ?? char).join("");
  if (stripped.length !== CERTIFICATE_CODE_LENGTH) return null;
  for (const char of stripped) {
    if (!CERTIFICATE_CODE_ALPHABET.includes(char)) return null;
  }
  return stripped;
}
function isValidCertificateCodeShape(input) {
  return normalizeCertificateCode(input) !== null;
}
function formatCertificateCode(code) {
  const canonical = normalizeCertificateCode(code);
  if (!canonical) return code ?? "";
  const groups = [];
  for (let i = 0; i < canonical.length; i += CERTIFICATE_CODE_GROUP_SIZE) {
    groups.push(canonical.slice(i, i + CERTIFICATE_CODE_GROUP_SIZE));
  }
  return groups.join(CERTIFICATE_CODE_SEPARATOR);
}

// src/constants/completionCriteria.ts
var COMPLETION_CRITERIA = {
  NONE: "none",
  SCROLL: "scroll",
  VIDEO: "video",
  SCROLL_AND_VIDEO: "scroll_and_video"
};

// src/constants/courseAccessLevel.ts
var COURSE_ACCESS_LEVEL = {
  FREE_PREVIEW: "free_preview",
  FULL: "full"
};
var COURSE_ACCESS_LEVELS = [
  COURSE_ACCESS_LEVEL.FREE_PREVIEW,
  COURSE_ACCESS_LEVEL.FULL
];
function isFullAccess(level) {
  return level === COURSE_ACCESS_LEVEL.FULL;
}
function canAccessChapter(level, chapter) {
  if (isFullAccess(level)) return true;
  return Boolean(chapter?.isFreemium);
}
function accessLevelForFreeAcquisition(isFree) {
  return isFree ? COURSE_ACCESS_LEVEL.FULL : COURSE_ACCESS_LEVEL.FREE_PREVIEW;
}

// src/constants/courseEnrollment.ts
var COURSE_ENROLLMENT = {
  ENROLLED: "enrolled",
  NOT_ENROLLED: "not_enrolled",
  GROUP_ENROLLED: "group_enrolled"
};

// src/constants/credentialSignature.ts
var CREDENTIAL_SIGNATURE_WIDTH = 552;
var CREDENTIAL_SIGNATURE_HEIGHT = 129;
var CREDENTIAL_SIGNATURE_PATH = "M8.4 74.7L8.4 74.7Q13.7 74.3 19.9 72.2L19.9 72.2Q30.7 57.9 43.4 47.6Q56.1 37.4 65.2 36.7L65.2 36.7Q69.8 36.4 70 38.7L70 38.7Q70.2 41.8 63.6 48.1Q57 54.3 46.3 61.2Q35.6 68.1 25 72.4L25 72.4Q17.6 82.4 13.2 91.5Q8.9 100.6 9.3 106.3L9.3 106.3Q9.8 114.3 20 113.6L20 113.6Q25.7 113.2 32.3 109.1Q38.9 105 43.2 101.2L43.2 101.2Q44.1 100.4 44.9 100.4Q45.6 100.3 45.7 101.1L45.7 101.1Q45.7 101.6 45.3 102.1L45.3 102.1Q40.9 105.8 37.4 108.2Q33.9 110.7 28.2 113.2Q22.4 115.8 17.1 116.2L17.1 116.2Q4.5 117.1 3.8 107.4L3.8 107.4Q3 95.7 17.8 75L17.8 75Q13.1 76.6 8.7 77.2L8.7 77.2Q7.5 77 7.4 75.9Q7.4 74.9 8.4 74.7M62.3 39.7L62.3 39.7Q56.5 40.1 46.6 48.7Q36.7 57.3 27.3 69.3L27.3 69.3Q36.4 65.4 45.4 59.4Q54.5 53.4 59.8 48.3Q65.2 43.1 65 40.9L65 40.9Q64.9 39.5 62.3 39.7M59.9 106.3L59.9 106.3Q59.8 104.8 61 102.3Q62.3 99.8 62.4 99.4Q62.5 99 62.2 99Q61.9 99 61.4 99.6Q60.9 100.2 55.6 104.2Q50.3 108.2 48.2 108.4L48.2 108.4Q46.8 108.5 46.7 106.7Q46.5 104.9 51.6 97Q56.7 89.1 59 89L59 89Q59.5 89 59.9 89.4Q60.4 89.8 60.4 90Q60.5 90.3 55.7 96.5Q51 102.8 51.1 104.5L51.1 104.5Q51.2 104.9 51.6 104.8L51.6 104.8Q53.5 104.7 59.4 99.2Q65.3 93.7 67.8 90.7Q70.2 87.7 71.7 87.6Q73.2 87.5 73.3 88.8L73.3 88.8Q73.3 89.6 72.8 90.4Q72.3 91.3 70.8 93.4Q69.3 95.6 68 97.5L68 97.5Q64.4 102.7 64.5 104Q64.6 105.2 65.7 105.2Q66.7 105.1 70.6 102L70.6 102Q77.8 96.5 83.8 91L83.8 91L85.8 89Q86.5 88.5 87 88.4Q87.5 88.4 87.5 89L87.5 89L87.6 89.3Q88 89.3 82.5 94.1Q77.1 98.8 70.9 103.7Q64.6 108.5 62.9 108.7L62.9 108.7Q60.1 108.9 59.9 106.3M105.7 89.4L107.7 87.6Q108.4 87 109 87Q109.5 87 109.6 87.7L109.6 87.7L109.6 87.9Q102.5 94.4 95.6 100.1Q88.7 105.9 85.3 106.1L85.3 106.1Q82.8 106.3 82.6 103.6Q82.5 100.9 84.7 98Q86.9 95.2 89.1 93Q91.3 90.8 91.2 90.2Q91.2 89.5 90.4 88.7Q89.6 87.9 89.1 87.7L89.1 87.7Q87.5 89.5 82.1 94.4L82.1 94.4L81.6 92.9Q87.5 87.5 88.4 85.7L88.4 85.7L88.3 84.8Q88.2 82.3 88.6 81.4Q89 80.5 90.8 78.7Q92.6 77 93.3 77Q94 76.9 94.6 77.4Q95.2 77.8 95.2 78.3Q95.2 78.8 94.9 79.5Q94.6 80.2 94.3 80.6Q94 81.1 93.5 81.8Q92.9 82.5 92.8 82.8Q92.6 83 92.5 83.6Q92.4 84.2 91.9 84.7Q91.5 85.1 91.5 85.7Q91.6 86.3 92.5 88.3Q93.4 90.3 93.5 91.2Q93.6 92.2 90.1 96Q86.7 99.8 86.8 102L86.8 102Q86.9 103.2 88 103.1L88 103.1Q89.4 103 93 100.2L93 100.2L94.2 99.4Q100.3 94.4 105.7 89.4L105.7 89.4M124.8 88.6L127.4 86.2Q128.2 85.6 128.8 85.6Q129.3 85.6 129.3 86.3L129.3 86.3L129.4 86.5Q122.1 93.5 114 99.8Q105.9 106.1 103.6 106.2Q101.3 106.4 99.7 105.6Q98.1 104.9 98 103.8Q97.9 102.7 98.5 101.9Q99 101.1 99.5 101.1Q100 101.1 101.3 102.5Q102.5 104 103.6 103.9L103.6 103.9Q105.9 103.7 106.9 98.8Q107.8 93.8 107.7 91.9Q107.6 90.1 107.3 90.1Q107.1 90.1 106.9 90.3L106.9 90.3L104.1 92.8L103.6 91.4Q109.6 85.9 110.5 84.2L110.5 84.2Q110.9 82.5 112 82.5L112 82.5Q112.7 82.4 112.8 82.9Q112.8 83.4 112.2 85.1Q111.6 86.8 110.9 92.7Q110.2 98.5 110.2 99.4Q110.3 100.3 110.8 100.3L110.8 100.3Q117.5 95.5 124.8 88.6L124.8 88.6M135.8 93.6L135.8 93.6Q141.4 93.2 148.7 86.4L148.7 86.4L150.4 86.8Q141.2 95.7 134.2 96.2L134.2 96.2Q133.4 96.2 132.9 96.1L132.9 96.1Q130.5 99.6 127.4 101.9Q124.4 104.2 122 104.3L122 104.3Q118.2 104.6 117.9 100.4Q117.6 96.1 124.2 88.5L124.2 88.5Q122.8 85.7 122.6 82.5Q122.3 79.3 124.5 77.2Q126.7 75 129.7 74.8Q132.7 74.6 135 76.5Q137.2 78.3 137.5 82L137.5 82Q137.9 87.7 134.6 93.6L134.6 93.6Q135 93.7 135.8 93.6M132.5 92.9L132.5 92.9Q135.2 87.5 135 83.8Q134.7 80.2 132.8 78.6Q130.9 76.9 129.2 77.1L129.2 77.1Q125.7 77.3 126 80.9Q126.2 84.5 128.1 87.9Q129.9 91.3 132.5 92.9M122 99.5L122 99.5Q122.1 101.5 124.7 101.3Q127.3 101.1 130.9 95.6L130.9 95.6Q128.1 94.4 125.8 91L125.8 91Q121.7 96.1 122 99.5M167.7 85.6L170.3 83.2Q171.1 82.6 171.6 82.6Q172.2 82.6 172.2 83.3L172.2 83.3L172.3 83.5Q165 90.5 156.9 96.8Q148.8 103.1 146.5 103.2Q144.2 103.4 142.6 102.6Q141 101.9 140.9 100.8Q140.8 99.7 141.4 98.9Q141.9 98.1 142.4 98.1Q142.9 98.1 144.2 99.5Q145.4 101 146.5 100.9L146.5 100.9Q148.8 100.7 149.8 95.8Q150.7 90.8 150.6 88.9Q150.5 87.1 150.2 87.1Q150 87.1 149.8 87.3L149.8 87.3L147 89.8L146.5 88.4Q152.5 82.9 153.4 81.2L153.4 81.2Q153.8 79.5 154.9 79.5L154.9 79.5Q155.6 79.4 155.7 79.9Q155.7 80.4 155.1 82.1Q154.5 83.8 153.8 89.7Q153.1 95.5 153.1 96.4Q153.2 97.3 153.7 97.3L153.7 97.3Q160.4 92.5 167.7 85.6L167.7 85.6M197.7 70.5L197.7 70.5Q197.9 73 207.1 74.6L207.1 74.6Q212.4 75.4 219.8 75.7L219.8 75.7Q210 68.1 200.9 68.7L200.9 68.7Q197.6 69 197.7 70.5M239.1 94.3L239.1 94.3Q241.2 94.2 248.3 77.1L248.3 77.1Q237.1 78.1 234.4 78.3Q231.7 78.5 226.7 78.7L226.7 78.7Q233 85.3 237.3 93.1L237.3 93.1Q238 94.4 239.1 94.3M265.6 24.6L265.6 24.6Q262.3 24.8 257.3 32.3Q252.3 39.7 244.2 53.2Q236 66.7 228.9 75.7L228.9 75.7Q235.9 75.4 241.1 75Q246.3 74.7 249.5 74.3L249.5 74.3Q255.6 59.8 261.3 43.8Q266.9 27.7 266.7 25.4L266.7 25.4Q266.7 24.5 265.6 24.6M254.9 73.7L254.9 73.7L275.5 70.7Q276.3 70.7 276.4 71.5Q276.4 72.3 275.1 73.3Q273.9 74.3 270 74.8Q266.1 75.4 253.6 76.6L253.6 76.6Q244.8 97.1 240.6 97.4L240.6 97.4Q237.8 97.6 236.4 95.2L236.4 95.2Q231.4 86.6 225.1 80.4L225.1 80.4L222.4 83.4Q208.6 98.2 188.9 99.5L188.9 99.5Q183.9 99.9 180.1 99.4Q176.3 98.9 176.3 97.6L176.3 97.6Q176.2 96.7 177.3 96.7L177.3 96.7Q178 96.6 179 96.9L179 96.9Q182.1 97.9 190.4 97.3L190.4 97.3Q205.2 96.3 217.7 83.7L217.7 83.7Q222.2 78.9 222.3 78.8L222.3 78.8Q212.8 78.7 206.3 77.4L206.3 77.4Q194.7 75 194.4 70.6L194.4 70.6Q194.3 69.1 195.9 68Q197.5 66.9 200.2 66.7L200.2 66.7Q212 65.9 223.4 75.8L223.4 75.8Q224.3 75.7 224.8 75.8L224.8 75.8Q233 65.6 244.5 47.1L244.5 47.1Q248.6 40.7 252.7 34.7L252.7 34.7Q261.2 22.3 267.2 21.9L267.2 21.9Q271.1 21.6 271.3 25L271.3 25Q271.5 27.3 266.1 43.4Q260.8 59.4 254.9 73.7M298 76.3L300.3 74.1Q301.1 73.6 301.6 73.5Q302.2 73.5 302.2 74.2L302.2 74.2L302.2 74.4Q294.8 81.9 286.8 87.8Q278.8 93.7 273.2 94.1L273.2 94.1Q268.9 94.4 268.5 89.1Q268.1 83.9 274 77.3Q279.9 70.6 287.3 70.1L287.3 70.1Q289.5 70.2 290.1 70.6Q290.7 71.1 290.7 72Q290.8 72.9 289.1 75.3Q287.4 77.8 285.9 77.9L285.9 77.9Q284.2 77.7 284.2 76.8L284.2 76.8Q284.1 76.4 285 75.5L285 75.5Q286.6 73.9 286.5 73.2Q286.5 72.5 285.4 72.6L285.4 72.6Q281.9 72.9 277 78.9Q272.1 84.9 272.4 88.6L272.4 88.6Q272.6 91.4 276.2 91.1Q279.8 90.9 283.8 88L283.8 88Q290.9 83 298 76.3L298 76.3M311.1 78L311.1 78Q310.5 78 310.5 77.6Q310.5 77.1 311.1 76.5L311.1 76.5Q314.2 73.5 314.1 71.3L314.1 71.3Q314 70 311.5 69.9L311.5 69.9Q307 70.3 301.2 77.2Q295.4 84 295.6 87.2L295.6 87.2Q295.7 88.3 296.9 88.2Q298.2 88.1 300.2 86.3L300.2 86.3Q302 84.9 306.8 79.8L306.8 79.8Q307.9 78.5 309 78.4L309 78.4Q310.4 78.3 310.5 79.6L310.5 79.6Q310.6 80.1 310.3 80.4Q310 80.8 309.3 81.8Q308.5 82.8 308 83.6L308 83.6Q306.4 86.3 306.4 87.1L306.4 87.1Q306.6 89.1 308.2 89L308.2 89Q310.3 88.9 315.8 84.6Q321.3 80.4 325.8 76.1L325.8 76.1L330.3 72Q331 71.5 331.6 71.4Q332.2 71.4 332.3 72.1L332.3 72.1L332.3 72.3Q325 79.3 316.9 85.6Q308.8 91.9 306.1 92.1L306.1 92.1Q302.9 92.3 302.7 88.9L302.7 88.9Q302.6 88.1 302.8 87.4Q303.1 86.7 303.1 86.4Q303.1 86.1 302.9 86L302.9 86Q302.6 86 299.2 88.9Q295.8 91.9 293.2 92.1Q290.6 92.2 290.4 89.4Q290.2 86.7 293.6 81.7Q297 76.7 303 72.3Q309 68 315 67.6L315 67.6Q316.4 67.5 317.4 68.1Q318.4 68.7 318.5 70.2Q318.6 71.6 315.9 74.7Q313.2 77.8 311.1 78M337.8 73.8L337.8 73.8L340.6 68.7Q340.3 68.6 340 68.6L340 68.6Q335.8 68.9 329.5 75.8Q323.2 82.6 323.3 85.2L323.3 85.2Q323.4 85.9 324.2 85.9L324.2 85.9Q326.7 85.7 331.5 81.1Q336.3 76.5 337.8 73.8M359.9 70.1L359.9 70.3Q340.3 88.6 335.9 88.9L335.9 88.9Q334.2 89 334.1 87Q334 85 335.1 80.2L335.1 80.2Q335.1 80.1 334.9 80.1L334.9 80.1Q324.1 90.4 320.3 90.7L320.3 90.7Q317.7 90.8 317.6 88.9L317.6 88.9Q317.3 84.8 325.8 76Q334.2 67.3 339.6 67L339.6 67Q340.3 66.9 341.2 67.8L341.2 67.8Q343.2 64.3 364.3 29.2L364.3 29.2Q366.6 26 368.2 25.9Q369.7 25.8 369.8 26.8L369.8 26.8Q370 29.2 354.3 51.5L354.3 51.5Q348.4 59.9 343.7 68.9Q339 77.9 339.3 82.4L339.3 82.4Q339.4 84.3 340.5 84.2L340.5 84.2Q341.8 84.1 345 81.6L345 81.6Q351.4 76.3 356.2 71.8L356.2 71.8L358 70.1Q358.6 69.5 359.2 69.5Q359.8 69.5 359.9 70.1L359.9 70.1M381.3 70.6L383.6 68.3Q384.3 67.7 384.9 67.7Q385.5 67.7 385.5 68.4L385.5 68.4L385.5 68.6Q375.8 77.9 369.2 82.6Q362.6 87.4 357 87.8L357 87.8Q352.5 88.1 352.2 83.7L352.2 83.7Q351.8 77.2 357.9 69.9Q364 62.6 368.5 62.3L368.5 62.3Q371 62.2 371.2 65L371.2 65Q371 70.5 356.4 80.8L356.4 80.8L356.5 81.8Q356.7 85.1 360.2 84.9Q363.6 84.6 367.4 82.1L367.4 82.1Q374.9 76.9 381.3 70.6L381.3 70.6M366.1 65.6L366.1 65.6Q363.9 65.8 360.7 69.7Q357.5 73.6 356.7 78.4L356.7 78.4Q361.2 75.2 364.3 71.7Q367.4 68.2 367.3 66.9Q367.2 65.5 366.1 65.6M384.2 67.4L384.2 67.4L383.9 66.4Q383.8 65.9 385.9 65.7L385.9 65.7Q387.1 65.6 387.2 67.1Q387.3 68.6 384.3 74Q381.2 79.4 381.2 79.6Q381.2 79.8 381.6 79.8Q382 79.7 390.3 72.1Q398.7 64.5 400.5 64.4Q402.3 64.3 402.4 65.5Q402.5 66.8 401.6 68.4Q400.7 70 397.8 74.3Q394.9 78.5 394.7 78.8Q394.5 79.2 394.5 79.6Q394.6 80 395.2 79.9Q395.9 79.9 404.6 72.4Q413.3 65 414.6 64.9Q415.9 64.8 415.9 65.9L415.9 65.9Q416.1 67.9 412 73.2Q408 78.5 408.1 79.9Q408.2 81.2 409.2 81.1Q410.3 81.1 414.3 78L414.3 78Q421.4 72.5 427.3 66.9L427.3 66.9L429.4 65Q430.2 64.4 430.7 64.4Q431.3 64.4 431.3 65L431.3 65L431.3 65.3Q411.5 84.3 406.5 84.6L406.5 84.6Q403.6 84.8 403.5 82.4L403.5 82.4Q403.3 80.4 405.9 76.4Q408.6 72.3 408.5 71.9Q408.5 71.6 408.3 71.6Q408 71.6 400.5 77.8Q392.9 84.1 391.4 84.2L391.4 84.2Q390.7 84.2 390.2 83.7Q389.7 83.2 389.6 82.6L389.6 82.6Q389.5 80.8 393.4 75L393.4 75Q397.3 69.3 397.3 68.8L397.3 68.8Q397.3 68.3 396.7 68.4L396.7 68.4Q396 68.4 383.3 80.3L383.3 80.3L378.5 84.7Q377.3 85.9 376.7 85.9Q376 86 375.4 85.7Q374.8 85.3 374.7 84.6Q374.7 84 376.2 81.1Q377.8 78.3 380.3 74.2L380.3 74.2Q384.2 68.1 384.2 67.4M473 12.6L472.3 13.9Q467.5 19.3 452.1 46.2L452.1 46.2Q466 42.6 469.4 42.3Q472.7 42.1 476.4 43.2Q480.1 44.2 480.2 45.8L480.2 45.8Q480.2 46.4 479.7 46.8Q479.2 47.3 478.6 47.3Q478.1 47.3 473.4 46.3Q468.7 45.2 464.5 45.5Q460.2 45.8 451 48.1L451 48.1Q434.6 76.2 429.1 81.9L429.1 81.9Q426.3 84.8 424.4 84.9L424.4 84.9Q422.7 85 422.5 82.6L422.5 82.6Q422.4 81.3 423 79.4Q423.5 77.4 424.7 77.3L424.7 77.3Q425.1 77.3 425.3 78.2Q425.5 79 425.7 79L425.7 79Q427.7 78.8 432.6 71.3Q437.5 63.8 446 49.4L446 49.4Q434.7 52.4 428.7 52.8L428.7 52.8Q417.3 53.6 416.9 47.6Q416.4 41.5 426 31.5Q435.5 21.5 449.5 13.3Q463.6 5.2 474.5 4.4L474.5 4.4Q482.2 3.9 482.5 8L482.5 8Q482.7 11.2 479.1 15.4Q475.4 19.6 473.1 19.7L473.1 19.7Q472.5 19.8 472.5 19Q472.4 18.3 472.8 17.9L472.8 17.9Q474.8 16.8 476.5 14.5Q478.2 12.2 478.1 10.3L478.1 10.3Q477.8 6.7 470.4 7.2Q462.9 7.7 451.1 14.9Q439.2 22 430.7 30.9Q422.2 39.8 422.6 45.2Q423 50.5 433.4 49.8L433.4 49.8Q437.4 49.5 447.3 47.2L447.3 47.2Q456.9 31.2 461.3 24.6L461.3 24.6Q469.2 12.6 472.5 12.4L472.5 12.4Q473 12.3 473 12.6L473 12.6M464.1 51.8L464.1 51.8Q464.2 54.3 473.5 56L473.5 56Q478.7 56.8 486.2 57.1L486.2 57.1Q476.3 49.5 467.3 50.1L467.3 50.1Q464 50.3 464.1 51.8M505.4 75.7L505.4 75.7Q507.5 75.6 514.7 58.5L514.7 58.5Q503.5 59.5 500.8 59.7Q498 59.9 493 60L493 60Q499.4 66.7 503.6 74.4L503.6 74.4Q504.3 75.8 505.4 75.7M531.9 6L531.9 6Q528.6 6.2 523.6 13.6Q518.6 21.1 510.5 34.6Q502.4 48.1 495.2 57.1L495.2 57.1Q502.2 56.8 507.4 56.4Q512.7 56.1 515.9 55.6L515.9 55.6Q522 41.2 527.6 25.1Q533.3 9.1 533.1 6.8L533.1 6.8Q533 5.9 531.9 6M521.2 55.1L521.2 55.1L541.9 52.1Q542.7 52.1 542.7 52.9Q542.8 53.7 541.5 54.6Q540.2 55.6 536.3 56.2Q532.5 56.8 519.9 58L519.9 58Q511.1 78.5 506.9 78.8L506.9 78.8Q504.2 79 502.8 76.6L502.8 76.6Q497.8 68 491.4 61.8L491.4 61.8L488.7 64.7Q474.9 79.5 455.3 80.9L455.3 80.9Q450.3 81.3 446.5 80.8Q442.7 80.3 442.6 79L442.6 79Q442.5 78.1 443.6 78L443.6 78Q444.3 78 445.4 78.3L445.4 78.3Q448.4 79.3 456.7 78.7L456.7 78.7Q471.6 77.7 484 65.1L484 65.1Q488.5 60.3 488.6 60.1L488.6 60.1Q479.2 60.1 472.7 58.8L472.7 58.8Q461.1 56.4 460.8 52L460.8 52Q460.7 50.5 462.2 49.4Q463.8 48.2 466.5 48.1L466.5 48.1Q478.4 47.2 489.8 57.2L489.8 57.2Q490.7 57.1 491.1 57.2L491.1 57.2Q499.3 47 510.9 28.5L510.9 28.5Q514.9 22.1 519 16.1L519 16.1Q527.6 3.7 533.5 3.3L533.5 3.3Q537.4 3 537.7 6.4L537.7 6.4Q537.8 8.7 532.5 24.7Q527.1 40.8 521.2 55.1M16.3 114.2C77.2 124 391.5 98 510.4 77.7C530.1 73.3 543.8 68.3 549.4 62.9C542 70.4 526.4 77.6 508.6 81.2C391.8 101.5 77.3 126.5 16.3 114.2Z";
var CREDENTIAL_SIGNATURE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CREDENTIAL_SIGNATURE_WIDTH} ${CREDENTIAL_SIGNATURE_HEIGHT}" fill="currentColor" aria-hidden="true" focusable="false"><path d="${CREDENTIAL_SIGNATURE_PATH}"/></svg>`;

// src/constants/credentialType.ts
var CREDENTIAL_TYPE = {
  NONE: "none",
  CONSTANCIA: "constancia",
  CERTIFICADO: "certificado"
};
var CREDENTIAL_TYPES = [
  CREDENTIAL_TYPE.NONE,
  CREDENTIAL_TYPE.CONSTANCIA,
  CREDENTIAL_TYPE.CERTIFICADO
];
var ISSUABLE_CREDENTIAL_TYPES = [
  CREDENTIAL_TYPE.CONSTANCIA,
  CREDENTIAL_TYPE.CERTIFICADO
];
function issuesCredential(type) {
  return type === CREDENTIAL_TYPE.CONSTANCIA || type === CREDENTIAL_TYPE.CERTIFICADO;
}
var CREDENTIAL_LABEL = {
  [CREDENTIAL_TYPE.CONSTANCIA]: "Constancia de finalizaci\xF3n",
  [CREDENTIAL_TYPE.CERTIFICADO]: "Certificado"
};
var CREDENTIAL_LABEL_SHORT = {
  [CREDENTIAL_TYPE.CONSTANCIA]: "constancia",
  [CREDENTIAL_TYPE.CERTIFICADO]: "certificado"
};

// src/constants/entityTypes.ts
var ENTITY_TYPES = {
  COURSE: "course",
  CHAPTER: "chapter",
  LESSON: "lesson",
  QUESTION: "question",
  NEWS: "news",
  ARTICLES: "articles",
  USER: "user",
  CATEGORY: "category",
  ANNOUNCEMENT: "announcement",
  GLOBAL_SETTINGS: "global_settings"
};

// src/constants/fileTypes.ts
var ALLOWED_LESSON_IMAGE_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/tiff"
];
var ALLOWED_VIDEO_FILE_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/ogg",
  "video/avi",
  "video/mov",
  "video/wmv"
];
var ALLOWED_PRESENTATION_FILE_TYPES = [
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.presentation"
];
var ALLOWED_EXCEL_FILE_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv"
];
var ALLOWED_WORD_FILE_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
var ALLOWED_PDF_FILE_TYPES = ["application/pdf"];

// src/constants/languages.ts
var SUPPORTED_LANGUAGES = {
  EN: "en",
  ES: "es"
};

// src/constants/lessonCompletionThresholds.ts
var VIDEO_END_TOLERANCE_SECONDS = 2;
var SCROLL_END_THRESHOLD = 0.985;
var EMBEDDED_VIDEO_END_FRACTION = 0.985;

// src/constants/lessonTypes.ts
var LESSON_TYPES = {
  CONTENT: "content",
  QUIZ: "quiz",
  AI_MENTOR: "ai_mentor",
  EMBED: "embed"
};

// src/constants/loginPageDocuments.ts
var MAX_LOGIN_PAGE_DOCUMENTS = 3;
var LOGIN_PAGE_DOCUMENTS_FILE_TYPES = ["application/pdf"];

// src/constants/newsSettings.ts
var ALLOWED_NEWS_SETTINGS = {
  NEWS_ENABLED: "newsEnabled",
  UNREGISTERED_USER_NEWS_ACCESSIBILITY: "unregisteredUserNewsAccessibility"
};

// src/constants/orderItemOutcome.ts
var ORDER_ITEM_OUTCOME = {
  /** Se le otorgó el acceso en esta orden. */
  ENROLLED: "enrolled",
  /** Ya tenía este acceso desde antes; la orden no cambió nada. */
  ALREADY_OWNED: "already_owned",
  /** Reserva de un curso que todavía no abrió (pre-inscripción). */
  PREINSCRIBED: "preinscribed",
  /** Tenía free preview y esta orden lo subió a full. */
  UPGRADED: "upgraded",
  /** Ítem pago todavía no cobrado: se entrega cuando el pago se aprueba. */
  PENDING_PAYMENT: "pending_payment",
  /** El acceso otorgado por esta orden fue revocado (reembolso / contracargo). */
  REVOKED: "revoked"
};
var ORDER_ITEM_OUTCOMES = [
  ORDER_ITEM_OUTCOME.ENROLLED,
  ORDER_ITEM_OUTCOME.ALREADY_OWNED,
  ORDER_ITEM_OUTCOME.PREINSCRIBED,
  ORDER_ITEM_OUTCOME.UPGRADED,
  ORDER_ITEM_OUTCOME.PENDING_PAYMENT,
  ORDER_ITEM_OUTCOME.REVOKED
];
var DELIVERED_OUTCOMES = [
  ORDER_ITEM_OUTCOME.ENROLLED,
  ORDER_ITEM_OUTCOME.UPGRADED
];
var ORDER_STATUS = {
  PENDING: "pending",
  AWAITING_PAYMENT: "awaiting_payment",
  /** La orden se creó pero no pudimos entregarle el link de pago por WhatsApp. */
  DELIVERY_FAILED: "delivery_failed",
  PROCESSING: "processing",
  COMPLETED: "completed",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
  REFUNDED: "refunded",
  /**
   * Contracargo: el titular de la tarjeta desconoció el pago ante su banco. Es un estado
   * que reporta MercadoPago y que revoca el acceso igual que un reembolso, pero no es lo
   * mismo: acá la plata se va sin que la hayamos devuelto nosotros.
   */
  CHARGED_BACK: "charged_back"
};
var ORDER_STATUSES = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.AWAITING_PAYMENT,
  ORDER_STATUS.DELIVERY_FAILED,
  ORDER_STATUS.PROCESSING,
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.REJECTED,
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.REFUNDED,
  ORDER_STATUS.CHARGED_BACK
];
var ORDER_PAYABLE_STATUSES = [
  ORDER_STATUS.PENDING,
  ORDER_STATUS.AWAITING_PAYMENT,
  ORDER_STATUS.DELIVERY_FAILED,
  ORDER_STATUS.PROCESSING
];
function isOrderPayable(status) {
  return ORDER_PAYABLE_STATUSES.includes(status);
}

// src/constants/qaSettings.ts
var ALLOWED_QA_SETTINGS = {
  QA_ENABLED: "QAEnabled",
  UNREGISTERED_USER_QA_ACCESSIBILITY: "unregisteredUserQAAccessibility"
};

// src/constants/tracking.ts
var TRACKING_COOKIES = {
  /** Primer y último toque (UTM + click IDs), base64url de JSON, 90 días. */
  ATTRIBUTION: "acd_attr",
  /** Id anónimo del navegador (UUID), 1 año. */
  ANONYMOUS_ID: "acd_aid",
  /** Consentimiento: `a=<0|1>&m=<0|1>&v=<versión>&t=<epoch s>`. a = analítica, m = publicidad. */
  CONSENT: "acd_consent"
};
var UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content"
];
var CLICK_ID_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "ttclid",
  "twclid",
  "li_fat_id",
  "msclkid"
];
var CONVERSION_EVENT = {
  /** Pidió el código para registrarse (teléfono enviado). */
  LEAD: "lead",
  /** Validó el código y creó la cuenta: el "registro verificado". */
  SIGN_UP: "sign_up",
  /** Se inscribió sin pagar (curso gratis o vista previa). */
  ENROLL_FREE: "enroll_free",
  /** Pago aprobado en MercadoPago. */
  PURCHASE: "purchase",
  /** Terminó un curso. */
  COURSE_COMPLETED: "course_completed",
  /** Evento de prueba que manda el botón del admin. */
  TEST: "test"
};
var CONVERSION_EVENT_NAMES = Object.values(CONVERSION_EVENT);
var PLATFORM_EVENT_NAMES = {
  meta: {
    lead: "Lead",
    sign_up: "CompleteRegistration",
    enroll_free: "EnrollFreeCourse",
    purchase: "Purchase",
    course_completed: "CourseCompleted",
    test: "Lead"
  },
  tiktok: {
    // TikTok renombró SubmitForm → Lead y CompletePayment → Purchase (los viejos siguen
    // andando porque TikTok los convierte, pero las configuraciones nuevas usan los actuales).
    lead: "Lead",
    sign_up: "CompleteRegistration",
    enroll_free: "EnrollFreeCourse",
    purchase: "Purchase",
    course_completed: "CourseCompleted",
    test: "Lead"
  },
  ga4: {
    lead: "generate_lead",
    sign_up: "sign_up",
    enroll_free: "enroll_course",
    purchase: "purchase",
    course_completed: "course_completed",
    test: "academia_test_event"
  }
};
var conversionEventId = {
  /**
   * Id del PRIMER intento de registro de ese teléfono (reenviar el código no genera otro Lead).
   * El navegador no lo puede calcular: lo devuelve `POST /api/auth/send-otp` en `leadEventId`.
   */
  lead: (registrationAttemptId) => `lead:${registrationAttemptId}`,
  signUp: (userId) => `reg:${userId}`,
  enrollFree: (orderId, courseId) => `enroll:${orderId}:${courseId}`,
  purchase: (orderId) => `purchase:${orderId}`,
  courseCompleted: (userId, courseId) => `complete:${userId}:${courseId}`
};
var TRACKING_DESTINATION = {
  META: "meta",
  TIKTOK: "tiktok",
  GA4: "ga4",
  GOOGLE_ADS: "google_ads",
  X: "x",
  LINKEDIN: "linkedin"
};
var TRACKING_DESTINATIONS = Object.values(TRACKING_DESTINATION);
var CONVERSION_DELIVERY_STATUS = {
  PENDING: "pending",
  SENT: "sent",
  FAILED: "failed",
  /** No se mandó a propósito: sin consentimiento, sin identificadores o sin mapeo del evento. */
  SKIPPED: "skipped"
};

// src/constants/tus.ts
var DEFAULT_TUS_CHUNK_SIZE = 50 * 1024 * 1024;
var DEFAULT_TUS_TTL_MS = 4 * 60 * 60 * 1e3;

// src/constants/whatsappMessage.ts
var WHATSAPP_MESSAGE_KIND = {
  /** Código de verificación del login. Su contenido NUNCA se persiste en claro. */
  OTP: "otp",
  /** Link de pago de una orden. Es el único reenviable. */
  PAYMENT_LINK: "payment_link",
  /** Mensaje de texto libre (fuera de template). */
  TEXT: "text"
};
var WHATSAPP_MESSAGE_KINDS = [
  WHATSAPP_MESSAGE_KIND.OTP,
  WHATSAPP_MESSAGE_KIND.PAYMENT_LINK,
  WHATSAPP_MESSAGE_KIND.TEXT
];
var WHATSAPP_MESSAGE_STATUS = {
  /** Fila escrita antes de disparar el request; todavía no sabemos nada. */
  PENDING: "pending",
  /** Kapso aceptó el envío y nos devolvió un wamid. */
  SENT: "sent",
  /** Meta confirmó la entrega en el dispositivo. */
  DELIVERED: "delivered",
  /** El destinatario abrió el mensaje. */
  READ: "read",
  /** Meta lo rechazó o no pudo entregarlo (trae código de error). */
  FAILED: "failed",
  /** El request a Kapso falló de nuestro lado (red, credenciales, 4xx/5xx). */
  ERROR: "error"
};
var WHATSAPP_MESSAGE_STATUSES = [
  WHATSAPP_MESSAGE_STATUS.PENDING,
  WHATSAPP_MESSAGE_STATUS.SENT,
  WHATSAPP_MESSAGE_STATUS.DELIVERED,
  WHATSAPP_MESSAGE_STATUS.READ,
  WHATSAPP_MESSAGE_STATUS.FAILED,
  WHATSAPP_MESSAGE_STATUS.ERROR
];
var WHATSAPP_STATUS_RANK = {
  [WHATSAPP_MESSAGE_STATUS.PENDING]: 0,
  [WHATSAPP_MESSAGE_STATUS.ERROR]: 1,
  [WHATSAPP_MESSAGE_STATUS.SENT]: 2,
  [WHATSAPP_MESSAGE_STATUS.DELIVERED]: 3,
  [WHATSAPP_MESSAGE_STATUS.READ]: 4,
  /**
   * `failed` gana sobre todo: si Meta terminó reportando una falla, eso es lo que hay que
   * mostrarle a soporte aunque antes hubiera pasado por `sent`.
   */
  [WHATSAPP_MESSAGE_STATUS.FAILED]: 5
};
function isWhatsAppStatusAdvance(current, next) {
  const currentRank = WHATSAPP_STATUS_RANK[current] ?? -1;
  return WHATSAPP_STATUS_RANK[next] > currentRank;
}
var WEBHOOK_PROVIDER = {
  KAPSO: "kapso",
  MERCADOPAGO: "mercadopago"
};

// src/types/onboarding.ts
var OnboardingPages = /* @__PURE__ */ ((OnboardingPages2) => {
  OnboardingPages2["DASHBOARD"] = "dashboard";
  OnboardingPages2["COURSES"] = "courses";
  OnboardingPages2["ANNOUNCEMENTS"] = "announcements";
  OnboardingPages2["PROFILE"] = "profile";
  OnboardingPages2["SETTINGS"] = "settings";
  OnboardingPages2["PROVIDER_INFORMATION"] = "providerInformation";
  return OnboardingPages2;
})(OnboardingPages || {});

// src/types/videoUploadTypes.ts
var VIDEO_UPLOAD_STATUS = {
  QUEUED: "queued",
  UPLOADED: "uploaded",
  PROCESSED: "processed",
  FAILED: "failed"
};
var VIDEO_PROVIDERS = {
  BUNNY: "bunny",
  S3: "s3"
};

// src/utils/coursePricing.ts
function isCourseFree(course) {
  if (!course) return false;
  return (course.priceInCents ?? 0) === 0 && !course.mercadopagoProductId;
}
function isCourseReservation(course) {
  return course?.isPreinscription === true;
}
function isCoursePaid(course) {
  return !isCourseFree(course);
}
function courseChargeableCents(course, currency) {
  if (!course || isCourseFree(course) || isCourseReservation(course)) return 0;
  const useMercadoPago = (currency ?? "").toUpperCase() === "ARS";
  const mp = course.mercadopagoPriceInCents ?? 0;
  const base = course.priceInCents ?? 0;
  return useMercadoPago && mp > 0 ? mp : base;
}
function cartChargeableCents(items, currency) {
  if (!items?.length) return 0;
  return items.reduce((sum, item) => sum + courseChargeableCents(item, currency), 0);
}
function splitByPricing(items) {
  const free = [];
  const paid = [];
  for (const item of items ?? []) {
    (isCourseFree(item) ? free : paid).push(item);
  }
  return { free, paid };
}
function splitByCheckoutKind(items) {
  const reservations = [];
  const free = [];
  const paid = [];
  for (const item of items ?? []) {
    if (isCourseReservation(item)) reservations.push(item);
    else if (isCourseFree(item)) free.push(item);
    else paid.push(item);
  }
  return { reservations, free, paid };
}

// src/utils/credentialText.ts
var CREDENTIAL_TIME_ZONE = "America/Argentina/Buenos_Aires";
var CREDENTIAL_SIGNER = "Cursos AcademIA";
var CREDENTIAL_ISSUED_LABEL = "Fecha de emisi\xF3n";
var CREDENTIAL_ACHIEVEMENT = "por completar con \xE9xito el curso";
function credentialLabel(type) {
  return issuesCredential(type) ? CREDENTIAL_LABEL[type] : "Credencial";
}
function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
function credentialLabelShort(type) {
  return issuesCredential(type) ? CREDENTIAL_LABEL_SHORT[type] : "credencial";
}
function credentialCopy(type) {
  const label = credentialLabel(type);
  const short = credentialLabelShort(type);
  const masculine = type === CREDENTIAL_TYPE.CERTIFICADO;
  return {
    label,
    short,
    thisIsYour: `${masculine ? "Este" : "Esta"} es tu ${short}`,
    issued: masculine ? "emitido" : "emitida",
    issuedOn: masculine ? "Emitido el" : "Emitida el",
    valid: masculine ? "v\xE1lido" : "v\xE1lida",
    preamble: masculine ? "Se otorga el presente a" : "Se otorga la presente a",
    official: `${capitalize(short)} oficial`
  };
}
var PG_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/i;
function parseCredentialInstant(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const trimmed = value.trim();
  const match = PG_TIMESTAMP.exec(trimmed);
  let iso = trimmed;
  if (match) {
    const [, day, time, zone] = match;
    let offset = "Z";
    if (zone && zone.toUpperCase() !== "Z") {
      const digits = zone.replace(":", "");
      offset = `${digits.slice(0, 3)}:${digits.slice(3) || "00"}`;
    }
    iso = `${day}T${time}${offset}`;
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
var longDateFormat;
var dayKeyFormat;
function tryFormat(options, locale) {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: CREDENTIAL_TIME_ZONE });
  } catch {
    return null;
  }
}
function formatCredentialDate(value) {
  const parsed = parseCredentialInstant(value);
  if (!parsed) return null;
  if (longDateFormat === void 0) {
    longDateFormat = tryFormat({ day: "numeric", month: "long", year: "numeric" }, "es-AR");
  }
  return longDateFormat ? longDateFormat.format(parsed) : parsed.toISOString().slice(0, 10);
}
function credentialDayKey(value) {
  const parsed = parseCredentialInstant(value);
  if (!parsed) return null;
  if (dayKeyFormat === void 0) {
    dayKeyFormat = tryFormat({ year: "numeric", month: "2-digit", day: "2-digit" }, "en-CA");
  }
  return dayKeyFormat ? dayKeyFormat.format(parsed) : parsed.toISOString().slice(0, 10);
}
function isSameCredentialDay(a, b) {
  const left = credentialDayKey(a);
  const right = credentialDayKey(b);
  return !left || !right || left === right;
}
function credentialDates({
  issuedAt,
  completedAt
}) {
  const finished = issuedAt && completedAt && !isSameCredentialDay(completedAt, issuedAt) ? formatCredentialDate(completedAt) : null;
  return {
    label: CREDENTIAL_ISSUED_LABEL,
    value: formatCredentialDate(issuedAt) ?? formatCredentialDate(completedAt),
    finishedNote: finished ? `Curso terminado el ${finished}` : null
  };
}
function credentialSignature(type) {
  return { name: CREDENTIAL_SIGNER, caption: credentialCopy(type).official };
}
function credentialPaperText({
  type,
  issuedAt,
  completedAt
}) {
  return {
    ...credentialCopy(type),
    achievement: CREDENTIAL_ACHIEVEMENT,
    dates: credentialDates({ issuedAt, completedAt }),
    signature: credentialSignature(type)
  };
}

// src/utils/lessonCompletionGate.ts
var CONTENT = "content";
var EMBED = "embed";
function usableMediaUrl(value) {
  if (typeof value !== "string") return null;
  const url = value.trim();
  if (!url || url === "null" || url === "undefined") return null;
  if (!/^(https?:\/\/|\/\/|\/)/i.test(url)) return null;
  return url;
}
function stripEmbeddedVideos(html) {
  return html.replace(/<figure\b[^>]*>[\s\S]*?<video\b[\s\S]*?<\/video>[\s\S]*?<\/figure>/gi, "").replace(/<video\b[\s\S]*?<\/video>/gi, "");
}
function hasVideoTag(html) {
  return /<video[\s>]/i.test(html);
}
function hasReadableMaterial(html) {
  const withoutVideos = stripEmbeddedVideos(html).replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "").replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "").replace(/<svg[^>]*>[\s\S]*?<\/svg>/gi, "");
  const visibleText = withoutVideos.replace(/<[^>]*>/g, " ").replace(/&nbsp;|&#160;|&#xA0;/gi, " ").replace(/\s+/g, " ").trim();
  return visibleText.length > 0 || /<(img|table|iframe|audio|canvas)\b/i.test(withoutVideos);
}
function resolveLessonCompletionGate(lesson, options = {}) {
  const type = lesson.type ?? CONTENT;
  const description = lesson.description ?? "";
  const videoWebUrl = usableMediaUrl(lesson.videoWebUrl);
  const fileVideoUrl = lesson.fileType === "video" ? usableMediaUrl(lesson.fileUrl ?? void 0) : null;
  const videoFirst = type === CONTENT && Boolean(videoWebUrl);
  const embeddedVideo = options.canObserveEmbeddedVideo ?? (hasVideoTag(description) && type === CONTENT);
  const hasVideo = Boolean(
    videoFirst || fileVideoUrl || embeddedVideo || lesson.hasVideo || lesson.hasOnlyVideo
  );
  const gated = type === CONTENT || type === EMBED;
  if (!gated) {
    return {
      hasVideo,
      videoFirst: false,
      requiresVideo: false,
      requiresReading: false,
      readingLocation: "page",
      noRequirements: false,
      completesOnVisit: false
    };
  }
  const requiresVideo = Boolean(videoFirst || fileVideoUrl || embeddedVideo && type === CONTENT);
  const explicitlyNoRequirements = lesson.completionCriteria === "none";
  const readableMaterial = hasReadableMaterial(description);
  const requiresReading = !requiresVideo && !explicitlyNoRequirements && readableMaterial;
  const noRequirements = !requiresVideo && !requiresReading;
  return {
    hasVideo,
    videoFirst,
    requiresVideo,
    requiresReading,
    // Mandar al alumno a un panel que no está en pantalla fue el bug original:
    // el panel "Material requerido" SÓLO existe en el layout video-first.
    readingLocation: videoFirst ? "panel" : "page",
    noRequirements,
    // NUNCA se completa una clase por el solo hecho de abrirla.
    //
    // Esto valía `!requiresVideo && !requiresReading` con una idea razonable —si no hay
    // nada verificable, no encerremos al alumno— y el resultado en la práctica fue
    // peor que el problema que resolvía: clases que se daban por vistas con apretar
    // play, y las siguientes autocompletándose en cadena mientras el alumno mira.
    //
    // Entre una clase que pide una acción de más y una que se regala, preferimos la
    // primera: perder una clase que el alumno hizo es recuperable, darle por vista una
    // que no vio no lo es. Una clase sin nada verificable es un DATO ROTO y se arregla
    // en el contenido (hay un validador de salud de curso para detectarlas), no
    // dándola por cumplida.
    completesOnVisit: false
  };
}

// src/utils/lessonKind.ts
var EMPTY_LESSON_BREAKDOWN = {
  classes: 0,
  questions: 0,
  quizzes: 0,
  evaluations: 0
};
var NON_CLASS_LESSON_TYPES = [
  LESSON_TYPES.QUIZ,
  LESSON_TYPES.AI_MENTOR
];
function lessonKindOf(type, questionCount) {
  if (type === LESSON_TYPES.AI_MENTOR) return "evaluation";
  if (type === LESSON_TYPES.QUIZ) return (questionCount ?? 0) > 1 ? "quiz" : "question";
  return "class";
}
function isClassLesson(type) {
  return !NON_CLASS_LESSON_TYPES.includes(type);
}
function countLessonKinds(items) {
  const breakdown = { ...EMPTY_LESSON_BREAKDOWN };
  for (const item of items) {
    const kind = lessonKindOf(item.type, item.questionCount);
    if (kind === "class") breakdown.classes += 1;
    else if (kind === "question") breakdown.questions += 1;
    else if (kind === "quiz") breakdown.quizzes += 1;
    else breakdown.evaluations += 1;
  }
  return breakdown;
}
var LESSON_KIND_WORDS = [
  ["classes", "clase", "clases"],
  ["questions", "pregunta", "preguntas"],
  ["quizzes", "quiz", "quizzes"],
  ["evaluations", "evaluaci\xF3n", "evaluaciones"]
];
function formatLessonBreakdown(breakdown) {
  const parts = LESSON_KIND_WORDS.flatMap(([key, singular, plural]) => {
    const count = breakdown?.[key] ?? 0;
    if (count <= 0) return [];
    return [`${count} ${count === 1 ? singular : plural}`];
  });
  return parts.length ? parts.join(" \xB7 ") : "0 clases";
}
function formatClassProgress(completed, total) {
  return `${completed} de ${total} ${total === 1 ? "clase" : "clases"}`;
}

// src/utils/personName.ts
var LOCALE = "es-AR";
var LOWERCASE_PARTICLES = /* @__PURE__ */ new Set(["de", "del", "la", "las", "los", "y"]);
var SEGMENT_SEPARATORS = /* @__PURE__ */ new Set(["-", "'", "\u2019"]);
var hasCase = (char) => char.toLocaleLowerCase(LOCALE) !== char.toLocaleUpperCase(LOCALE);
var upperChar = (char) => {
  const upper = char.toLocaleUpperCase(LOCALE);
  return Array.from(upper).length === 1 ? upper : char;
};
var capitalizeWord = (word) => {
  let result = "";
  let startsSegment = true;
  for (const char of Array.from(word)) {
    result += startsSegment ? upperChar(char) : char;
    startsSegment = SEGMENT_SEPARATORS.has(char);
  }
  return result;
};
function capitalizePersonName(value) {
  if (value === null || value === void 0) return value;
  let name = String(value).replace(/\s+/g, " ").trim();
  if (!name) return name;
  const isShouting = Array.from(name).some(hasCase) && name === name.toLocaleUpperCase(LOCALE);
  if (isShouting) name = name.toLocaleLowerCase(LOCALE);
  return name.split(" ").map(
    (word, index) => index > 0 && LOWERCASE_PARTICLES.has(word) ? word : capitalizeWord(word)
  ).join(" ");
}

// src/utils/richHtmlMarkers.ts
var RICH_HTML_MARKER_PATTERNS = [
  { marker: "style-tag", pattern: /<style[\s>/]/i },
  { marker: "script", pattern: /<script[\s>/]/i },
  { marker: "button", pattern: /<button[\s>/]/i },
  { marker: "svg", pattern: /<svg[\s>/]/i },
  // `<input>` NO va acá: la lista de tareas de TipTap emite `<input type="checkbox">`.
  { marker: "form-control", pattern: /<(?:form|select|textarea)[\s>/]/i },
  // El video nativo de clase (`<video class="lesson-video">`) lo maneja el renderer del alumno;
  // el embed de TipTap es un `<div data-node-type="video">`, nunca un `<video>`.
  { marker: "media", pattern: /<(?:video|audio|canvas)[\s>/]/i },
  { marker: "table", pattern: /<table[\s>/]/i },
  { marker: "inline-style", pattern: /<[a-z][^>]*\sstyle\s*=/i },
  { marker: "widget", pattern: /<[a-z][^>]*\sdata-widget\s*=/i }
];
function findRichHtmlMarkers(html) {
  if (!html) return [];
  return RICH_HTML_MARKER_PATTERNS.filter(({ pattern }) => pattern.test(html)).map(
    ({ marker }) => marker
  );
}
function lostRichHtmlMarkers({
  storedDescription,
  incomingDescription,
  descriptionEditor,
  discardRichContent,
  viaBrowserSession
}) {
  if (incomingDescription === void 0 || incomingDescription === null) return [];
  if (descriptionEditor === "html" || discardRichContent) return [];
  if (descriptionEditor !== "visual" && !viaBrowserSession) return [];
  const incoming = new Set(findRichHtmlMarkers(incomingDescription));
  return findRichHtmlMarkers(storedDescription).filter((marker) => !incoming.has(marker));
}

// src/utils/videoProgress.ts
var RANGE_JOIN_TOLERANCE_SECONDS = 0.25;
function mergeVideoWatchedRanges(...rangeSets) {
  const duration = rangeSets.reduce((current, set) => {
    const candidate = Number(set.durationSeconds);
    if (!Number.isFinite(candidate) || candidate <= 0) return current;
    return current === void 0 ? candidate : Math.min(current, candidate);
  }, void 0);
  const sanitized = rangeSets.flatMap((set) => set.ranges ?? []).map(([rawStart, rawEnd]) => {
    const start = Math.max(0, Number(rawStart));
    const end = duration === void 0 ? Number(rawEnd) : Math.min(duration, Number(rawEnd));
    return [start, end];
  }).filter(
    ([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start
  ).sort(([startA, endA], [startB, endB]) => startA - startB || endA - endB);
  const merged = [];
  for (const [start, end] of sanitized) {
    const previous = merged.at(-1);
    if (!previous || start > previous[1] + RANGE_JOIN_TOLERANCE_SECONDS) {
      merged.push([start, end]);
      continue;
    }
    previous[1] = Math.max(previous[1], end);
  }
  return merged;
}
function videoWatchedSeconds(ranges) {
  return mergeVideoWatchedRanges({ ranges }).reduce(
    (total, [start, end]) => total + end - start,
    0
  );
}
export {
  ACCESS_GUARD,
  AI_MENTOR_TYPE,
  ALLOWED_AGE_LIMITS,
  ALLOWED_ARTICLES_SETTINGS,
  ALLOWED_AVATAR_IMAGE_TYPES,
  ALLOWED_EXCEL_FILE_TYPES,
  ALLOWED_EXTENSIONS,
  ALLOWED_LESSON_IMAGE_FILE_TYPES,
  ALLOWED_NEWS_SETTINGS,
  ALLOWED_PDF_FILE_TYPES,
  ALLOWED_PRESENTATION_FILE_TYPES,
  ALLOWED_QA_SETTINGS,
  ALLOWED_VIDEO_FILE_TYPES,
  ALLOWED_WORD_FILE_TYPES,
  ARTICLE_STATUS,
  AUTHORIZATION_ERROR_CODES,
  CERTIFICATE_CODE_ALPHABET,
  CERTIFICATE_CODE_GROUP_SIZE,
  CERTIFICATE_CODE_LENGTH,
  CERTIFICATE_CODE_SEPARATOR,
  CLICK_ID_PARAMS,
  COMPLETION_CRITERIA,
  CONVERSION_DELIVERY_STATUS,
  CONVERSION_EVENT,
  CONVERSION_EVENT_NAMES,
  COURSE_ACCESS_LEVEL,
  COURSE_ACCESS_LEVELS,
  COURSE_ENROLLMENT,
  CREDENTIAL_ACHIEVEMENT,
  CREDENTIAL_ISSUED_LABEL,
  CREDENTIAL_LABEL,
  CREDENTIAL_LABEL_SHORT,
  CREDENTIAL_SIGNATURE_HEIGHT,
  CREDENTIAL_SIGNATURE_PATH,
  CREDENTIAL_SIGNATURE_SVG,
  CREDENTIAL_SIGNATURE_WIDTH,
  CREDENTIAL_SIGNER,
  CREDENTIAL_TIME_ZONE,
  CREDENTIAL_TYPE,
  CREDENTIAL_TYPES,
  DEFAULT_TUS_CHUNK_SIZE,
  DEFAULT_TUS_TTL_MS,
  DELIVERED_OUTCOMES,
  EMBEDDED_VIDEO_END_FRACTION,
  EMPTY_LESSON_BREAKDOWN,
  ENTITY_TYPES,
  ISSUABLE_CREDENTIAL_TYPES,
  LESSON_TYPES,
  LOGIN_PAGE_DOCUMENTS_FILE_TYPES,
  MAX_LOGIN_PAGE_DOCUMENTS,
  NON_CLASS_LESSON_TYPES,
  ORDER_ITEM_OUTCOME,
  ORDER_ITEM_OUTCOMES,
  ORDER_PAYABLE_STATUSES,
  ORDER_STATUS,
  ORDER_STATUSES,
  OnboardingPages,
  PLATFORM_EVENT_NAMES,
  RICH_HTML_MARKER_PATTERNS,
  SCROLL_END_THRESHOLD,
  SUPPORTED_LANGUAGES,
  TRACKING_COOKIES,
  TRACKING_DESTINATION,
  TRACKING_DESTINATIONS,
  UTM_PARAMS,
  VIDEO_END_TOLERANCE_SECONDS,
  VIDEO_PROVIDERS,
  VIDEO_UPLOAD_STATUS,
  WEBHOOK_PROVIDER,
  WHATSAPP_MESSAGE_KIND,
  WHATSAPP_MESSAGE_KINDS,
  WHATSAPP_MESSAGE_STATUS,
  WHATSAPP_MESSAGE_STATUSES,
  accessLevelForFreeAcquisition,
  canAccessChapter,
  capitalizePersonName,
  cartChargeableCents,
  conversionEventId,
  countLessonKinds,
  courseChargeableCents,
  credentialCopy,
  credentialDates,
  credentialLabel,
  credentialLabelShort,
  credentialPaperText,
  credentialSignature,
  findRichHtmlMarkers,
  formatCertificateCode,
  formatClassProgress,
  formatCredentialDate,
  formatLessonBreakdown,
  isClassLesson,
  isCourseFree,
  isCoursePaid,
  isCourseReservation,
  isFullAccess,
  isOrderPayable,
  isSameCredentialDay,
  isValidCertificateCodeShape,
  isWhatsAppStatusAdvance,
  issuesCredential,
  lessonKindOf,
  lostRichHtmlMarkers,
  mergeVideoWatchedRanges,
  normalizeCertificateCode,
  parseCredentialInstant,
  resolveLessonCompletionGate,
  splitByCheckoutKind,
  splitByPricing,
  stripEmbeddedVideos,
  videoWatchedSeconds
};
